<script>
export default {
	name: 'PlanYourTripCard',
	inheritAttrs: false,
};
</script>

<script setup>
import AppHyperlink from '~/components/AppHyperlink.vue';
import { isUrlMatchCurrentHostname } from '~/logic/helpers/string.js';

const props = defineProps({
	keyVisualSrc: { type: String, default: '' },
	keyVisualAlignment: { type: String, default: 'left' },
	keyVisualAlt: { type: String, default: '' },
	gradientValue: { type: String, default: '' },
	gradientOpacity: { type: String, default: '30' },
	bgSrc: { type: String, default: '' },
	bgAlt: { type: String, default: '' },
	title: { type: String, default: '' },
	url: { type: String, default: '#' },
	openInNewTab: { type: Boolean, default: false },
	cardSize: { type: String, default: '' },
	componentIdAA: { type: String, default: '' },
});

const gradientMap = computed(() => {
	return {
		'cyan': `to ${props.keyVisualAlignment === 'left' ? 'right' : 'left'}, #90C8CD, var(--secondary-teal-base)`,
		'blue': `to ${props.keyVisualAlignment === 'left' ? 'right' : 'left'}, #9FADC3, var(--primary-blue-dark)`,
		'red': `to ${props.keyVisualAlignment === 'left' ? 'right' : 'left'}, #C4A8C1, var(--secondary-purple-light)`,
		'purple': `to ${props.keyVisualAlignment === 'left' ? 	'right' : 'left'}, #BDB2CD, var(--secondary-purple-dark)`,
		'mhh-teal': `to ${props.keyVisualAlignment === 'left' ? 'right' : 'left'}, #8BC7CE, var(--secondary-mhh-teal-light)`,
		'': '',
	};
});

const wrapperAttrs = computed(() => {
	if (props.url !== 'null') {
		return {
			href: props.url,
			target: props.openInNewTab ? '_blank' : '_self',
			showExternalLinkIcon: false,
		};
	}
	return null;
});

</script>


<template>
<component
	:is="props.url !== 'null' ? AppHyperlink : 'div'"
	v-bind="wrapperAttrs"
	v-aa="[
		{
			clickName: props.title,
			clickComponentType: 'URL',
			componentName: 'PlanYourTripCard',
			componentID: props.componentIdAA,
		},
		{
			name: props.title,
			type: isUrlMatchCurrentHostname(props.url) ? 'other' : 'exit',
		},
	]"
	class="PlanYourTripCard w-full pt-5 w-full"
	:class="`${cardSize} ${props.url !== 'null' && 'hover:no-underline'}`"
>
	<div
		class="inner-wrapper flex flex-row justify-bottom items-center rounded-xl bg-gray-700 w-full h-[118px] relative"
		:class="{
			'flex-row-reverse': props.keyVisualAlignment === 'right',
		}"
	>
		<div class="bg-element-wrapper absolute rounded-xl overflow-hidden w-full h-full">
			<div
				class="bg-element-gradient absolute top-0 left-0 z-20 w-full h-full"
				:style="{
					backgroundImage: `linear-gradient(${gradientMap[props.gradientValue]})`,
					opacity: gradientMap[props.gradientValue] !== '' && gradientOpacity ? `${parseInt(gradientOpacity) / 100}` : '1',
				}"
				aria-hidden="true"
			></div>
			<img
				v-if="bgSrc !== 'null'"
				class="bg-element-img w-full h-full object-cover object-center"
				:src="bgSrc"
				:alt="bgAlt"
			/>
		</div>
		<div class="key-visual-wrapper w-[115px] h-full relative z-20">
			<img
				v-if="keyVisualSrc !== 'null'"
				class="key-visual-img absolute bottom-0 left-1/2 transform -translate-x-2/4 w-full"
				:src="keyVisualSrc"
				:alt="keyVisualAlt"
			/>
		</div>
		<p class="card-text heading-6 w-[calc(100%-115px)] leading-6 text-white relative z-20 my-4 px-4 line-clamp-4">{{ title }}</p>
	</div>
</component>
</template>
 
<style lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';
.PlanYourTripCard {
	display: block;
}
</style>
